import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { systemService } from '../../services/system.service';
import { AppVersionConfig } from '../../types/system';
import { getErrorMsg } from '../../utils/error';

const EMPTY: AppVersionConfig = {
  iosMinSupportedVersion: '', iosLatestVersion: '', iosStoreUrl: '',
  androidMinSupportedVersion: '', androidLatestVersion: '', androidStoreUrl: '',
  forceTitle: '', forceMessage: '', softTitle: '', softMessage: '', shareBaseUrl: '',
};

const AppVersionPage = () => {
  const { t } = useTranslation('common');
  const [config, setConfig] = useState<AppVersionConfig>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setConfig(await systemService.getAppVersionConfig());
      } catch (e) {
        setError(getErrorMsg(e, 'Failed to load setting'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (key: keyof AppVersionConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setConfig((c) => ({ ...c, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      setConfig(await systemService.setAppVersionConfig(config));
      setSuccess(t('saved'));
    } catch (e) {
      setError(getErrorMsg(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title={t('app_version_settings')}>
      <div className="space-y-6 max-w-5xl">
        {error && <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">{error}</div>}
        {success && <div className="p-3 rounded-md bg-green-50 text-green-600 border border-green-100">{success}</div>}

        {/* Top row: iOS | Android | Share Link — 3-up on large screens, stacks on small */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold">{t('app_version_ios')}</h2>
            <Input label={t('app_version_min_supported')} value={config.iosMinSupportedVersion} onChange={update('iosMinSupportedVersion')} disabled={loading} placeholder="1.2.0" />
            <Input label={t('app_version_latest')} value={config.iosLatestVersion} onChange={update('iosLatestVersion')} disabled={loading} placeholder="1.4.0" />
            <Input label={t('app_version_store_url')} value={config.iosStoreUrl} onChange={update('iosStoreUrl')} disabled={loading} placeholder="https://apps.apple.com/..." />
          </Card>

          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold">{t('app_version_android')}</h2>
            <Input label={t('app_version_min_supported')} value={config.androidMinSupportedVersion} onChange={update('androidMinSupportedVersion')} disabled={loading} placeholder="1.2.0" />
            <Input label={t('app_version_latest')} value={config.androidLatestVersion} onChange={update('androidLatestVersion')} disabled={loading} placeholder="1.4.0" />
            <Input label={t('app_version_store_url')} value={config.androidStoreUrl} onChange={update('androidStoreUrl')} disabled={loading} placeholder="https://play.google.com/..." />
          </Card>

          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold">{t('app_version_share')}</h2>
            <Input label={t('app_version_share_base_url')} value={config.shareBaseUrl} onChange={update('shareBaseUrl')} disabled={loading} placeholder="https://share.marine2go.com" />
          </Card>
        </div>

        {/* Full-width: update messages (4 fields) */}
        <Card className="p-5 space-y-4">
          <h2 className="text-base font-semibold">{t('app_version_messages')}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('app_version_messages_hint')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label={t('app_version_force_title')} value={config.forceTitle} onChange={update('forceTitle')} disabled={loading} />
            <Input label={t('app_version_force_message')} value={config.forceMessage} onChange={update('forceMessage')} disabled={loading} />
            <Input label={t('app_version_soft_title')} value={config.softTitle} onChange={update('softTitle')} disabled={loading} />
            <Input label={t('app_version_soft_message')} value={config.softMessage} onChange={update('softMessage')} disabled={loading} />
          </div>
        </Card>

        <Button variant="primary" onClick={handleSave} disabled={loading || saving}>
          {saving ? t('saving') : t('save')}
        </Button>
      </div>
    </Layout>
  );
};

export default AppVersionPage;
